import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const Modal = styled(motion.div)`
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(18px);
  border-radius: 22px;
  padding: 30px 32px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  color: #2c3e50;
  box-shadow: 0 8px 40px 0 rgba(0,0,0,0.25), 0 1.5px 8px 0 rgba(0,0,0,0.10);
  border: 2.5px solid rgba(220, 220, 220, 0.85);
  position: relative;

  @media (max-width: 768px) {
    padding: 22px 10px;
    max-width: 90vw;
  }

  @media (max-width: 480px) {
    padding: 14px 2vw;
    max-width: 98vw;
    border-radius: 14px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #7f8c8d;
  transition: color 0.3s ease;
  
  &:hover {
    color: #e74c3c;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 10px;
  text-align: center;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;

  @media (max-width: 480px) {
    font-size: 1.7rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #7f8c8d;
  margin-bottom: 30px;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 30px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  padding: 4px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  background: ${props => props.active ? '#4ecdc4' : 'transparent'};
  color: ${props => props.active ? 'white' : '#7f8c8d'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#4ecdc4' : 'rgba(78, 205, 196, 0.1)'};
  }
`;

const SocialSection = styled.div`
  margin-bottom: 25px;
`;

const GoogleButton = styled(motion.button)`
  width: 100%;
  padding: 15px;
  border: 2px solid #e0e0e0;
  background: white;
  color: #2c3e50;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s ease;
  margin-bottom: 15px;
  
  &:hover {
    border-color: #4285f4;
    background: #f8f9fa;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(66, 133, 244, 0.2);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const GoogleIcon = styled.div`
  width: 20px;
  height: 20px;
  background: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19.6 10.23c0-.82-.1-1.42-.25-2.05H10v3.72h5.5c-.15.96-.74 2.31-2.04 3.22v2.45h3.16c1.89-1.73 2.98-4.3 2.98-7.34z' fill='%234285F4'/%3E%3Cpath d='M10 20c2.7 0 4.96-.89 6.62-2.41l-3.16-2.45c-.89.63-2.06.98-3.46.98-2.65 0-4.92-1.8-5.74-4.21H1.07v2.52C2.72 17.75 6.09 20 10 20z' fill='%2334A853'/%3E%3Cpath d='M4.26 11.92c-.22-.63-.35-1.29-.35-1.92s.13-1.29.35-1.92V5.56H1.07C.39 6.85 0 8.37 0 10s.39 3.15 1.07 4.44l3.19-2.52z' fill='%23FBBC05'/%3E%3Cpath d='M10 3.98c1.51 0 2.59.65 3.19 1.19l2.33-2.33C14.96.78 12.7 0 10 0 6.09 0 2.72 2.25 1.07 5.56l3.19 2.52C5.08 5.78 7.35 3.98 10 3.98z' fill='%23EA4335'/%3E%3C/svg%3E") no-repeat center;
  background-size: contain;
`;

const Divider = styled.div`
  text-align: center;
  margin: 20px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #e0e0e0;
  }
  
  span {
    background: rgba(255, 255, 255, 0.95);
    padding: 0 15px;
    color: #7f8c8d;
    font-size: 0.9rem;
    position: relative;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #34495e;
`;

const Input = styled.input`
  padding: 15px;
  border: 2px solid ${props => props.error ? '#e74c3c' : '#e0e0e0'};
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#e74c3c' : '#4ecdc4'};
    transform: translateY(-2px);
    box-shadow: 0 4px 15px ${props => props.error ? 'rgba(231, 76, 60, 0.2)' : 'rgba(78, 205, 196, 0.2)'};
  }

  &::placeholder {
    color: #bdc3c7;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 0.85rem;
  margin-top: 5px;
  font-weight: 500;
`;

const Button = styled(motion.button)`
  padding: 15px;
  background: linear-gradient(45deg, #4ecdc4, #45b7d1);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(78, 205, 196, 0.3);
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PremiumBadge = styled.div`
  background: linear-gradient(45deg, #f39c12, #e67e22);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
`;

const AuthModal = ({ isOpen, onClose, defaultTab = 'login' }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  const { login, signup, signInWithGoogle } = useAuth();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (activeTab === 'signup') {
      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      let result;
      if (activeTab === 'login') {
        result = await login(formData.email, formData.password);
      } else {
        result = await signup(formData.email, formData.password, formData.fullName);
      }

      if (result.success) {
        onClose();
        // Reset form
        setFormData({
          email: '',
          password: '',
          fullName: '',
          confirmPassword: ''
        });
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      setErrors({ general: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      const result = await signInWithGoogle();
      
      if (result.success) {
        // Don't close modal immediately as user will be redirected
        // onClose() will be called when auth state changes
      } else {
        setErrors({ general: result.error || 'Google sign in failed' });
      }
    } catch (error) {
      setErrors({ general: 'Google sign in failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setErrors({});
    setFormData({
      email: '',
      password: '',
      fullName: '',
      confirmPassword: ''
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>&times;</CloseButton>
          
          <Title>
            {activeTab === 'login' ? 'Welcome Back!' : 'Join Parteezy'}
          </Title>
          
          <Subtitle>
            {activeTab === 'login' 
              ? 'Sign in to create and manage your party rooms'
              : 'Create your account to host epic drinking games'
            }
          </Subtitle>

          <TabContainer>
            <Tab 
              active={activeTab === 'login'}
              onClick={() => switchTab('login')}
              type="button"
            >
              Sign In
            </Tab>
            <Tab 
              active={activeTab === 'signup'}
              onClick={() => switchTab('signup')}
              type="button"
            >
              Sign Up
            </Tab>
          </TabContainer>

          {/* Social Sign In Section */}
          <SocialSection>
            <GoogleButton
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGoogleSignIn}
              disabled={loading}
              type="button"
            >
              <GoogleIcon />
              {loading ? 'Signing in...' : `Continue with Google`}
            </GoogleButton>
            

            
            <Divider>
              <span>or continue with email</span>
            </Divider>
          </SocialSection>

          <Form onSubmit={handleSubmit}>
            {activeTab === 'signup' && (
              <InputGroup>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  name="fullName"
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  error={errors.fullName}
                />
                {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
              </InputGroup>
            )}

            <InputGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email}
              />
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            </InputGroup>

            <InputGroup>
              <Label>Password</Label>
              <Input
                type="password"
                name="password"
                placeholder={activeTab === 'login' ? 'Enter your password' : 'Create a password'}
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password}
              />
              {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
            </InputGroup>

            {activeTab === 'signup' && (
              <InputGroup>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={errors.confirmPassword}
                />
                {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
              </InputGroup>
            )}

            {errors.general && (
              <ErrorMessage style={{ textAlign: 'center', marginTop: 10 }}>
                {errors.general}
              </ErrorMessage>
            )}

            <Button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
            >
              {loading ? (
                <>
                  <LoadingSpinner />
                  {activeTab === 'login' ? 'Signing In...' : 'Creating Account...'}
                </>
              ) : (
                activeTab === 'login' ? 'Sign In with Email' : 'Create Account'
              )}
            </Button>
          </Form>

          {activeTab === 'signup' && (
            <PremiumBadge>
              ✨ Start with a FREE account, upgrade to Premium anytime!
            </PremiumBadge>
          )}
        </Modal>
      </Backdrop>
    </AnimatePresence>
  );
};

export default AuthModal; 